import { knowledgeBaseService } from "@/services/knowledgeBase.service/knowledgeBase.api";
import type { KnowledgeBase } from "@/types/knowledgeBase";
import { useEffect, useState } from "react";
import Select from "react-select";

interface KnowledgeBaseSelectProps {
  selectedKnowledgeBases: string[];
  onChange: (knowledgeBases: string[]) => void;
}

export default function KnowledgeBaseSelect({
  selectedKnowledgeBases,
  onChange,
}: KnowledgeBaseSelectProps) {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch knowledge bases
  useEffect(() => {
    const fetchKnowledgeBases = async () => {
      try {
        setLoading(true);
        const [response, error] = await knowledgeBaseService.getAll({}, 1, 100);

        if (error) {
          console.error("Error fetching knowledge bases:", error);
          setError("Erro ao carregar bases de conhecimento");
          return;
        }

        if (response && response.result) {
          setKnowledgeBases(response.result);
        }
      } catch (err) {
        console.error("Error fetching knowledge bases:", err);
        setError("Erro ao carregar bases de conhecimento");
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledgeBases();
  }, []);

  // Format knowledge bases for react-select
  const knowledgeBaseOptions = knowledgeBases
    .map((kb) => ({
      value: kb._id,
      label: kb.name,
    }));

  const selectedOptions = knowledgeBaseOptions.filter((option) =>
    selectedKnowledgeBases.includes(option.value)
  );

  const handleKnowledgeBaseChange = (selected: any) => {
    const selectedValues = selected
      ? selected.map((item: any) => item.value)
      : [];
    onChange(selectedValues);
  };

  // Custom styles for react-select to match the site design
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: "0.375rem",
      borderColor: state.isFocused ? "hsl(var(--ring))" : "hsl(var(--input))",
      boxShadow: state.isFocused ? "0 0 0 2px hsl(var(--ring))" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "hsl(var(--ring))" : "hsl(var(--input))",
      },
      background: "hsl(var(--background))",
      color: "#fff",
    }),
    menu: (provided: any) => ({
      ...provided,
      background: "hsl(var(--popover))",
      borderRadius: "0.375rem",
      border: "1px solid hsl(var(--border))",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      background: state.isSelected
        ? "hsl(var(--primary))"
        : state.isFocused
          ? "hsl(var(--accent))"
          : "transparent",
      color: state.isSelected
        ? "hsl(var(--primary-foreground))"
        : "hsl(var(--foreground))",
      cursor: "pointer",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      background: "hsl(var(--accent))",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "hsl(var(--accent-foreground))",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: "hsl(var(--accent-foreground))",
      ":hover": {
        background: "hsl(var(--destructive))",
        color: "hsl(var(--destructive-foreground))",
      },
    }),
  };

  return (
    <div className="w-full">
      <pre>
        { }
      </pre>
      <Select
        isMulti
        options={knowledgeBaseOptions}
        value={selectedOptions}
        onChange={handleKnowledgeBaseChange}
        styles={customStyles}
        placeholder={loading ? "Carregando..." : "Selecione bases de conhecimento"}
        isLoading={loading}
        isDisabled={loading}
        noOptionsMessage={() => "Nenhuma base de conhecimento encontrada"}
        loadingMessage={() => "Carregando..."}
      />
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
}
